import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import { LuxDataTable, useWindowResize } from '@lux/wardrobe-lib'
import SectionItemContainer from '@/app/components/Layout/SectionItemContainer/SectionItemContainer'
import { visitActionSurveyColumns } from './table-config'
import { useEffect, useState } from 'react'
import { FilterMatchMode } from 'primereact/api'
import {
    getVisitSurveyList,
    setActivitySectionStatus,
    setVisitSelectedSurveys,
} from '@/app/redux/actions/activity-creation'
import { NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE } from '@/app/redux/reducers/activity-creation'
import { ActivitySection, NewActivitySectionType } from '@/app/models/new-activity-section-type'
import { ActivityStep, VisitSurvey } from '@/app/models/new-activity-creation-state'
import './VisitAction.scss'
import { downloadVisitSurveyList } from '@/app/redux/actions/activity-execution'

const VisitAction = ({
    visitStoreId,
    selectedSurveys,
    surveyList,
    surveyListLoading,
    setVisitSelectedSurveys,
    getVisitSurveyList,
    downloadVisitSurveyList,
    setActivitySectionStatus,
}: {
    visitStoreId: string
    selectedSurveys: VisitSurvey[]
    surveyList: VisitSurvey[]
    surveyListLoading: boolean
    setVisitSelectedSurveys: (surveyList: VisitSurvey[]) => void
    getVisitSurveyList: (storeId: string) => void
    downloadVisitSurveyList: () => void
    setActivitySectionStatus: (
        activityKey: string,
        sectionKey: NewActivitySectionType,
        section: Partial<ActivityStep>
    ) => void
}) => {
    const componentClass = 'smart-new-activity-visit-action'

    const intl = useIntl()

    const isMobile = useWindowResize()

    const [filteredSurveyList, setFilteredSurveyList] = useState<VisitSurvey[]>([])
    const [filters, setFilters] = useState({
        surveyName: {
            value: '',
            matchMode: FilterMatchMode.CONTAINS,
            onChange: (v) => onChangeFilter(v, 'surveyName'),
        },
        surveyType: {
            value: '',
            matchMode: FilterMatchMode.CONTAINS,
            onChange: (v) => onChangeFilter(v, 'surveyType'),
        },
    })

    const onChangeFilter = (value, field) => {
        setFilters({
            ...filters,
            [field]: { ...filters[field], value: value },
        })
    }

    const onSelectionFilters = (whichFilter: 'show_all' | 'show_selected' | 'show_unselected') => {
        if (whichFilter === 'show_selected') {
            setFilteredSurveyList(selectedSurveys)
            return
        }
        if (whichFilter === 'show_unselected') {
            setFilteredSurveyList(
                surveyList.filter((s) => !selectedSurveys.find((su) => su.surveyId === s.surveyId))
            )
            return
        }
        setFilteredSurveyList(surveyList)
    }

    const selectionFilters = [
        {
            label: intl.formatMessage({
                id: 'modals.activity.create.distribution.action.show_all',
                defaultMessage: 'Show all',
            }),
            onClick: () => onSelectionFilters('show_all'),
        },
        {
            label: intl.formatMessage({
                id: 'modals.activity.create.distribution.action.show_selected',
                defaultMessage: 'Show selected',
            }),
            onClick: () => onSelectionFilters('show_selected'),
        },
        {
            label: intl.formatMessage({
                id: 'modals.activity.create.distribution.action.show_unselected',
                defaultMessage: 'Show unselected',
            }),
            onClick: () => onSelectionFilters('show_unselected'),
        },
    ]

    useEffect(() => {
        getVisitSurveyList(visitStoreId)
    }, [])

    useEffect(() => {
        setFilteredSurveyList(surveyList.sort((a, b) => a.surveyName.localeCompare(b.surveyName)))
    }, [surveyList])

    useEffect(() => {
        setActivitySectionStatus(ActivitySection.VISIT_ACTIVITY, NewActivitySectionType.ACTION, {
            isDone: selectedSurveys.length > 0,
            isDirty: !selectedSurveys.length,
        })
    }, [selectedSurveys])

    return (
        <div className={componentClass}>
            <SectionItemContainer hideFooter={true}>
                <LuxDataTable
                    label={intl.formatMessage({
                        id: 'modals.new.activity.section.action.survey.list',
                        defaultMessage: 'Store list',
                    })}
                    mandatory={true}
                    loading={surveyListLoading}
                    selectionMode={'multiple'}
                    columns={visitActionSurveyColumns(isMobile).map((column) => ({
                        ...column,
                        header: column.header
                            ? intl.formatMessage({
                                  id: column.header,
                                  defaultMessage: column.header.split('.').pop(),
                              })
                            : '',
                    }))}
                    rowsPerPageOptions={[5, 10, 15]}
                    dataSet={filteredSurveyList}
                    scrollable
                    selectedRows={selectedSurveys}
                    setSelectedRows={(value) => setVisitSelectedSurveys(value)}
                    actionsList={[
                        {
                            icon: 'caret-right',
                            command: (e) => window.open(`${e.surveyUrl}?readOnly=true`, '_blank'),
                        },
                    ]}
                    dataKey="surveyId"
                    columnFilters={filters}
                    labels={{
                        filter: intl.formatMessage({
                            id: 'modals.new_resource.distribution.store_list.filter',
                            defaultMessage: 'filter',
                        }),
                        rearrange: intl.formatMessage({
                            id: 'modals.new_resource.distribution.store_list.rearrange',
                            defaultMessage: 'rearrange',
                        }),
                        of: intl.formatMessage({
                            id: 'modals.new_resource.distribution.store_list.labels.of',
                            defaultMessage: 'of',
                        }),
                        selected: intl.formatMessage({
                            id: 'modals.new_resource.distribution.store_list.labels.selected',
                            defaultMessage: 'selected',
                        }),
                        emptyMessage: intl.formatMessage({
                            id: 'modals.new_resource.distribution.store_list.labels.emptyMessage',
                            defaultMessage: 'No results to show.',
                        }),
                    }}
                    onDownload={downloadVisitSurveyList}
                    selectionFilters={selectionFilters}
                />
            </SectionItemContainer>
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    visitStoreId:
        state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE][ActivitySection.VISIT_ACTIVITY].form.mainInfo
            .storeId.value,
    selectedSurveys: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].visitActivity.selectedSurveys,
    surveyList: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].visitActivity.surveyList,
    surveyListLoading:
        state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].visitActivity.surveyListLoading,
})

const mapDispatchToProps = (dispatch: any) => ({
    setVisitSelectedSurveys: (surveyList: VisitSurvey[]) =>
        dispatch(setVisitSelectedSurveys(surveyList)),
    getVisitSurveyList: (storeId: string) => dispatch(getVisitSurveyList(storeId)),
    downloadVisitSurveyList: () => dispatch(downloadVisitSurveyList()),
    setActivitySectionStatus: (
        activityKey: string,
        sectionKey: NewActivitySectionType,
        section: Partial<ActivityStep>
    ) => dispatch(setActivitySectionStatus(activityKey, sectionKey, section)),
})

export default connect(mapStateToProps, mapDispatchToProps)(VisitAction)
